import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPriceLocal as formatPrice, fixAssetUrl, canonicalBase } from "../lib/api";
import { useCart } from "../lib/cart";
import { ChevronLeft, ShoppingCart, Package, Download, Check, Gamepad2, X, ChevronLeft as CLeft, ChevronRight as CRight } from "lucide-react";
import SEO from "../components/SEO";
import RichTextView from "../components/RichTextView";
import NotFound from "./NotFound";
import { toast } from "sonner";

const CONDITION_LABEL = {
  new: "Nové – nerozbaleno",
  very_good: "Velmi dobrý stav",
  good: "Dobrý stav",
  acceptable: "Přijatelný stav",
};

export default function GamingProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(undefined);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(null); // index or null

  useEffect(() => {
    setProduct(undefined);
    setActiveImg(0);
    api.get(`/gaming/products/${slug}`).then((r) => setProduct(r.data || null)).catch(() => setProduct(null));
  }, [slug]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % (product?.images?.length || 1));
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + (product?.images?.length || 1)) % (product?.images?.length || 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, product]);

  if (product === null) return <NotFound/>;
  if (product === undefined) return <div className="pt-10 text-center text-zinc-500">Načítání…</div>;

  const inStock = (product.stock_qty ?? 0) > 0;
  const isPhysical = product.product_type === "physical";
  const onSale = product.price_original && product.price_original > product.price;
  const base = canonicalBase();
  const images = product.images && product.images.length > 0 ? product.images : [];

  const addNow = () => {
    if (!inStock) return toast.error("Skladem 0 ks");
    add({ ...product, kind: "gaming" }, qty);
    toast.success(`${product.name} přidáno do košíku`);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.seo_description || product.short_description,
    image: images.map((i) => fixAssetUrl(i)),
    brand: { "@type": "Brand", name: product.publisher || "Gaming" },
    sku: product.slug,
    offers: {
      "@type": "Offer",
      url: `${base}/gaming/${product.slug}`,
      priceCurrency: "CZK",
      price: product.price,
      availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: product.condition === "new" ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition",
    },
  };

  return (
    <div className="pt-6 pb-24" data-testid="gaming-detail">
      <SEO title={product.seo_title || product.name} description={product.seo_description || product.short_description} jsonLd={jsonLd}/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/gaming" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-6" data-testid="gaming-back-link"><ChevronLeft className="w-4 h-4"/> Zpět na Gaming</Link>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10">
          {/* Gallery */}
          <div>
            <div
              className="aspect-[4/3] rounded-3xl bg-zinc-50 overflow-hidden relative cursor-zoom-in group"
              onClick={() => images.length && setLightbox(activeImg)}
              data-testid="gaming-gallery-main"
            >
              {images[activeImg] ? (
                <img src={fixAssetUrl(images[activeImg])} alt={product.name} className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-300"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-300"><Gamepad2 className="w-24 h-24"/></div>
              )}
              {images.length > 0 && (
                <div className="absolute bottom-3 right-3 text-xs px-2 py-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">Klikněte pro zvětšení</div>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {images.slice(0, 5).map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${activeImg === i ? "border-purple-500 ring-2 ring-purple-500/20" : "border-zinc-200 hover:border-zinc-300"}`}
                    data-testid={`gaming-thumb-${i}`}
                  >
                    <img src={fixAssetUrl(im)} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {product.publisher && <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">{product.publisher}</div>}
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="gaming-detail-title">{product.name}</h1>

            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-zinc-900 text-white">
                {isPhysical ? <><Package className="w-3.5 h-3.5"/> Fyzické zboží</> : <><Download className="w-3.5 h-3.5"/> Digitální kód</>}
              </span>
              {product.condition && (
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${product.condition === "new" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                  {CONDITION_LABEL[product.condition] || product.condition}
                </span>
              )}
              {product.platforms?.map((pl) => (
                <span key={pl} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-purple-100 text-purple-700">{pl}</span>
              ))}
              {product.genres?.map((g) => (
                <span key={g} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700">{g}</span>
              ))}
            </div>

            {product.short_description && (
              <p className="text-zinc-700 mt-5 leading-relaxed">{product.short_description}</p>
            )}

            {/* Price + add */}
            <div className="mt-6 rounded-3xl border border-zinc-200 bg-white p-6">
              <div className="flex items-baseline gap-3">
                <div className={`font-display font-bold text-4xl ${onSale ? "text-rose-600" : ""}`} data-testid="gaming-detail-price">{formatPrice(product.price)}</div>
                {onSale && <div className="text-zinc-400 line-through">{formatPrice(product.price_original)}</div>}
              </div>
              <div className={`mt-1 text-sm font-semibold ${inStock ? "text-emerald-600" : "text-rose-500"}`} data-testid="gaming-detail-stock">
                {inStock ? `Skladem: ${product.stock_qty} ${product.stock_qty === 1 ? "kus" : "ks"}` : "Vyprodáno"}
              </div>
              {isPhysical && (
                <div className="text-xs text-zinc-500 mt-2">Doručení Českou poštou / Zásilkovnou za 99 Kč. U bazarových kusů expedujeme do 2 pracovních dnů.</div>
              )}

              <div className="flex items-center gap-3 mt-5">
                <div className="inline-flex items-center border border-zinc-200 rounded-full overflow-hidden">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 hover:bg-zinc-50" data-testid="gaming-qty-dec">−</button>
                  <span className="px-3 py-2 min-w-[2ch] text-center font-semibold" data-testid="gaming-qty">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="px-3 py-2 hover:bg-zinc-50" data-testid="gaming-qty-inc">+</button>
                </div>
                <button
                  onClick={addNow}
                  disabled={!inStock}
                  className="btn-primary flex-1 py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="gaming-add-to-cart-btn"
                >
                  <ShoppingCart className="w-4 h-4"/> Přidat do košíku
                </button>
              </div>
            </div>

            {product.features?.length > 0 && (
              <ul className="mt-6 space-y-2">
                {product.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-700"><Check className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0"/> {f}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Long description */}
        {product.description && (
          <div className="mt-12 max-w-3xl">
            <h2 className="font-display font-bold text-2xl mb-4">Popis</h2>
            <RichTextView html={product.description}/>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && images[lightbox] && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="gaming-lightbox">
          <button className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full" onClick={() => setLightbox(null)} data-testid="gaming-lightbox-close"><X className="w-6 h-6"/></button>
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + images.length) % images.length); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-3 hover:bg-white/10 rounded-full" data-testid="gaming-lightbox-prev"><CLeft className="w-6 h-6"/></button>
              <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % images.length); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-3 hover:bg-white/10 rounded-full" data-testid="gaming-lightbox-next"><CRight className="w-6 h-6"/></button>
            </>
          )}
          <img src={fixAssetUrl(images[lightbox])} alt="" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()}/>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">{lightbox + 1} / {images.length}</div>
        </div>
      )}
    </div>
  );
}
